/**
 * TokenTag Response Utilities
 * Handles normalization and storage of tokenTag API responses
 */

// Constants
export const TOKEN_TAG_RESPONSE_KEY = "tokenTagResponseData";

/**
 * Normalizes tokenTag API response to consistent structure
 * @param {Object} tagRes - Response from tokenTagRequest
 * @returns {Object} Normalized response object
 */
export const normalizeTagResponse = (tagRes) => {
  const response = tagRes?.response || {};
  const status = tagRes?.status || 0;
  
  // Determine success: 200 status OR success: true in response
  const isSuccess = status === 200 || response.success === true;
  
  // Extract message from various possible fields
  const message = response.message || 
                  response.error || 
                  response.detail || 
                  "An error occurred";
  
  // Extract reason if present
  const reason = response.reason || null;
  
  return {
    type: isSuccess ? "success" : "error",
    status: status,
    success: response.success || false,
    message: message,
    reason: reason,
    
    // Success-specific fields (only in 200 responses)
    pointsTransferred: response.points_transferred || null,
    targetPlayerId: response.target_player_id || null,
    targetPlayerName: response.target_player_name || null,
    targetEliminated: response.target_eliminated || false,
    gameEnded: response.game_ended || false,
    winner: response.winner || null,
    remainingPlayers: response.remaining_players || null,
    newScores: response.new_scores || null,
    
    // Error-specific fields
    error: response.error || null,
  };
};

/**
 * Handles tokenTag request response and stores in localStorage
 * @param {Object} tagRes - Response from tokenTagRequest
 * @param {Error} error - Optional error object for catch blocks
 */
export const handleTokenTagResponse = (tagRes, error = null) => {
  if (error) {
    // Network errors or unexpected errors
    const errorData = {
      type: "error",
      status: 500,
      success: false,
      message: error.message || "An unexpected error occurred",
      error: error.message
    };
    localStorage.setItem(
      TOKEN_TAG_RESPONSE_KEY,
      JSON.stringify(errorData)
    );
    return;
  }
  
  // Normalize the response
  const normalizedData = normalizeTagResponse(tagRes);
  
  // Store in localStorage
  localStorage.setItem(
    TOKEN_TAG_RESPONSE_KEY,
    JSON.stringify(normalizedData)
  );
};

/**
 * Retrieves and parses tokenTag response from localStorage
 * @returns {Object|null} Parsed response data or null
 */
export const getTokenTagResponseFromStorage = () => {
  const responseData = localStorage.getItem(TOKEN_TAG_RESPONSE_KEY);
  
  if (responseData) {
    try {
      return JSON.parse(responseData);
    } catch (error) {
      console.error("Error parsing tokenTag response data:", error);
      localStorage.removeItem(TOKEN_TAG_RESPONSE_KEY);
      return null;
    }
  }
  
  return null;
};

/**
 * Clears tokenTag response data from localStorage
 */
export const clearTokenTagResponse = () => {
  localStorage.removeItem(TOKEN_TAG_RESPONSE_KEY);
  // Also clear old key if it exists
  localStorage.removeItem("tokenTagSuccessData");
};

